<template>
  <div class="orders-container">
    <!-- 搜索筛选区 -->
    <el-card shadow="hover" class="filter-card">
      <el-row :gutter="16">
        <el-col :xs="24" :sm="8">
          <el-input 
            v-model="searchKeyword" 
            placeholder="搜索订单号/客户名称" 
            clearable 
            prefix-icon="Search"
            @keyup.enter="loadOrders(true)"
            @clear="loadOrders(true)"
          />
        </el-col>
        <el-col :xs="12" :sm="4">
          <el-select v-model="filterStatus" placeholder="订单状态" clearable @change="loadOrders(true)">
            <el-option label="全部" value="" />
            <el-option label="草稿" value="draft" />
            <el-option label="已确认" value="confirmed" />
            <el-option label="生产中" value="producing" />
            <el-option label="已发货" value="shipped" />
            <el-option label="已完成" value="completed" />
            <el-option label="已取消" value="cancelled" />
          </el-select>
        </el-col>
        <el-col :xs="12" :sm="4">
          <el-select v-model="filterType" placeholder="订单类型" clearable @change="loadOrders(true)">
            <el-option label="全部" value="" />
            <el-option label="标准订单" value="standard" />
            <el-option label="样品单" value="sample" />
            <el-option label="维修单" value="repair" />
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="8" style="text-align:right;">
          <el-button type="primary" icon="Plus" @click="showAddDialog">新建订单</el-button>
          <el-button icon="Refresh" @click="loadOrders(true)">刷新</el-button>
        </el-col>
      </el-row>
    </el-card>

    <!-- 订单列表 -->
    <el-card shadow="hover" class="list-card">
      <el-table v-if="!isMobile"
        :data="orders"
        stripe 
        border 
        style="width: 100%"
        v-loading="loading"
        :default-sort="{prop: 'createdate', order: 'descending'}"
      >
        <el-table-column prop="orderno" label="订单号" width="160" sortable />
        <el-table-column prop="ordertype" label="类型" width="100">
          <template #default="{row}">
            <el-tag :type="row.ordertype === 'sample' ? 'warning' : 'primary'">
              {{ row.ordertype === 'standard' ? '标准' : row.ordertype === 'sample' ? '样品' : '维修' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="companyname" label="客户名称" min-width="150" />
        <el-table-column prop="incoterms" label="贸易术语" width="100" />
        <el-table-column prop="totalamount" label="总金额" width="120" align="right">
          <template #default="{row}">
            {{ formatCurrency(row.totalamount, row.currency) }}
          </template>
        </el-table-column>
        <el-table-column prop="deposit" label="到款金额" width="120" align="right">
          <template #default="{row}">
            <span v-if="Number(row.deposit || 0) > 0" class="text-success">
              {{ formatCurrency(row.deposit, row.currency) }}
            </span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100" align="center">
          <template #default="{row}">
            <el-tag :type="getStatusType(row.status)">
              {{ getStatusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createdate" label="创建日期" width="110" sortable>
          <template #default="{row}">
            {{ formatDate(row.createdate) }}
          </template>
        </el-table-column>
        <el-table-column prop="estimatedshipdate" label="预计发货" width="110">
          <template #default="{row}">
            <span :class="{'overdue': isOverdue(row.estimatedshipdate)}">
              {{ formatDate(row.estimatedshipdate) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="{row}">
            <el-button type="primary" link size="small" @click="viewOrder(row)">查看</el-button>
            <el-button type="primary" link size="small" @click="editOrder(row)" v-if="row.status === 'draft'">编辑</el-button>
            <el-button type="success" link size="small" @click="confirmOrder(row)" v-if="row.status === 'draft'">确认</el-button>
            <el-button type="warning" link size="small" @click="updateStatus(row)" v-if="row.status !== 'cancelled' && row.status !== 'completed'">更新状态</el-button>
            <el-button type="danger" link size="small" @click="cancelOrder(row)" v-if="row.status === 'draft' || row.status === 'confirmed'">取消</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 移动端卡片视图 -->
      <div v-if="isMobile" class="mobile-cards">
        <div v-for="row in orders" :key="row.orderid" class="order-card"
          @click="viewOrder(row)">
          <div class="card-header">
            <el-tag :type="getStatusType(row.status)" size="small">{{ getStatusText(row.status) }}</el-tag>
            <span class="card-order-no">{{ row.orderno }}</span>
            <el-tag :type="row.ordertype === 'sample' ? 'warning' : 'primary'" size="small">
              {{ row.ordertype === 'standard' ? '标准' : row.ordertype === 'sample' ? '样品' : '维修' }}
            </el-tag>
          </div>
          <div class="card-body">
            <div class="card-field">
              <span class="field-label">客户</span>
              <span class="field-value">{{ row.companyname || '-' }}</span>
            </div>
            <div class="card-field">
              <span class="field-label">金额</span>
              <span class="field-value fw-bold">{{ formatCurrency(row.totalamount, row.currency) }}</span>
            </div>
            <div class="card-field">
              <span class="field-label">到款</span>
              <span class="field-value" :class="{ 'fw-bold': Number(row.deposit || 0) > 0 }">
                {{ Number(row.deposit || 0) > 0 ? formatCurrency(row.deposit, row.currency) : '-' }}
              </span>
            </div>
            <div class="card-field">
              <span class="field-label">术语</span>
              <span class="field-value">{{ row.incoterms || '-' }}</span>
            </div>
            <div class="card-field">
              <span class="field-label">发货</span>
              <span class="field-value" :class="{ 'text-danger': isOverdue(row.estimatedshipdate) }">
                {{ formatDate(row.estimatedshipdate) }}
              </span>
            </div>
          </div>
          <div class="card-footer">
            <span class="card-date">{{ formatDate(row.createdate) }}</span>
            <span class="card-actions" @click.stop>
              <el-button type="primary" link size="small" @click="viewOrder(row)">查看</el-button>
              <el-button v-if="row.status === 'draft'" type="success" link size="small" @click="confirmOrder(row)">确认</el-button>
            </span>
          </div>
        </div>
        <el-empty v-if="!orders.length" description="暂无订单" />
      </div>

      <!-- 分页 -->
      <el-pagination
        v-model:current-page="currentPage"
        v-model:page-size="pageSize"
        :page-sizes="[20, 50, 100]"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
        style="margin-top: 16px; justify-content: flex-end;"
        @size-change="loadOrders(true)"
        @current-change="loadOrders()"
      />
    </el-card>

    <!-- 新建/编辑订单对话框 -->
    <el-dialog 
      v-model="dialogVisible" 
      :title="isEdit ? '编辑订单' : '新建订单'" 
      width="900px"
      :close-on-click-modal="false"
    >
      <el-form ref="orderFormRef" :model="orderForm" :rules="orderRules" label-width="100px">
        <!-- 订单基本信息 -->
        <el-divider content-position="left">订单信息</el-divider>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="订单号" prop="orderno">
              <el-input v-model="orderForm.orderno" placeholder="自动生成或手动输入" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="订单类型" prop="ordertype">
              <el-select v-model="orderForm.ordertype" style="width:100%">
                <el-option label="标准订单" value="standard" />
                <el-option label="样品单" value="sample" />
                <el-option label="维修单" value="repair" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 客户信息 -->
        <el-divider content-position="left">客户信息</el-divider>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="客户" prop="companyid">
              <el-autocomplete
                v-model="customerSearchText"
                :fetch-suggestions="searchCustomers"
                placeholder="输入客户名称搜索"
                @select="handleCustomerSelect"
                style="width:100%"
                value-key="companyname"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="联系人" prop="contactid">
              <el-select v-model="orderForm.contactid" placeholder="选择联系人" style="width:100%">
                <el-option
                  v-for="contact in contactList"
                  :key="contact.contactid"
                  :label="contact.fullname"
                  :value="Number(contact.contactid)"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 贸易条款 -->
        <el-divider content-position="left">贸易条款</el-divider>
        <el-row :gutter="16">
          <el-col :span="8">
            <el-form-item label="贸易术语" prop="incoterms">
              <el-select v-model="orderForm.incoterms" style="width:100%">
                <el-option label="EXW" value="EXW" />
                <el-option label="FOB" value="FOB" />
                <el-option label="CIF" value="CIF" />
                <el-option label="CFR" value="CFR" />
                <el-option label="DDP" value="DDP" />
                <el-option label="DAP" value="DAP" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="付款方式" prop="paymentmethod">
              <el-select v-model="orderForm.paymentmethod" style="width:100%">
                <el-option label="T/T 30%定金" value="T/T 30% deposit" />
                <el-option label="T/T 100%预付" value="T/T 100% in advance" />
                <el-option label="信用证" value="L/C at sight" />
                <el-option label="D/P" value="D/P" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="币种" prop="currency">
              <el-select v-model="orderForm.currency" style="width:100%">
                <el-option label="USD" value="USD" />
                <el-option label="EUR" value="EUR" />
                <el-option label="CNY" value="CNY" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="16">
          <el-col :span="8">
            <el-form-item label="签发日期" prop="issuedate">
              <el-date-picker 
                v-model="orderForm.issuedate" 
                type="date" 
                placeholder="选择日期" 
                style="width:100%"
                value-format="YYYY-MM-DD"
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="预计发货" prop="estimatedshipdate">
              <el-date-picker 
                v-model="orderForm.estimatedshipdate" 
                type="date" 
                placeholder="选择日期" 
                style="width:100%"
                value-format="YYYY-MM-DD"
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="目的地" prop="destination">
              <el-input v-model="orderForm.destination" />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 产品明细 -->
        <el-divider content-position="left">
          产品明细
          <el-button type="primary" link @click="addProductRow" style="margin-left:8px;">+ 添加产品</el-button>
        </el-divider>
        
        <div v-for="(item, index) in orderForm.items" :key="index" class="product-row">
          <el-row :gutter="12" align="middle">
            <el-col :span="6">
              <el-form-item 
                :prop="`items.${index}.productcode`" 
                :rules="{required: true, message: '请选择产品', trigger: 'change'}"
                label-width="0"
              >
                <el-autocomplete
                  v-model="item.productcode"
                  :fetch-suggestions="searchProducts"
                  placeholder="产品型号"
                  @select="(val) => handleProductSelect(index, val)"
                  style="width:100%"
                  value-key="productcode"
                  size="small"
                />
              </el-form-item>
            </el-col>
            <el-col :span="5">
              <el-form-item label-width="0">
                <el-input v-model="item.description" placeholder="描述" size="small" />
              </el-form-item>
            </el-col>
            <el-col :span="3">
              <el-form-item 
                :prop="`items.${index}.quantity`"
                :rules="{required: true, message: '请输入数量', trigger: 'blur'}"
                label-width="0"
              >
                <el-input-number v-model="item.quantity" :min="1" size="small" style="width:100%" />
              </el-form-item>
            </el-col>
            <el-col :span="4">
              <el-form-item 
                :prop="`items.${index}.unitprice`"
                :rules="{required: true, message: '请输入单价', trigger: 'blur'}"
                label-width="0"
              >
                <el-input-number 
                  v-model="item.unitprice" 
                  :min="0" 
                  :precision="2" 
                  size="small" 
                  style="width:100%"
                  controls-position="right"
                />
              </el-form-item>
            </el-col>
            <el-col :span="3">
              <el-form-item label-width="0">
                <span class="subtotal">${{ (item.quantity * item.unitprice).toFixed(2) }}</span>
              </el-form-item>
            </el-col>
            <el-col :span="3">
              <el-form-item label-width="0">
                <el-button type="danger" link size="small" @click="removeProductRow(index)">删除</el-button>
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <!-- 金额汇总 -->
        <el-row :gutter="16" style="margin-top:16px;">
          <el-col :span="18" style="text-align:right;padding-right:30px;">
            <strong>合计金额:</strong>
          </el-col>
          <el-col :span="6">
            <strong class="total-amount">{{ formatCurrency(totalAmount, orderForm.currency) }}</strong>
          </el-col>
        </el-row>

        <!-- 到款金额（客户收款金额；>0 时作为生成 CI/PL 的前提） -->
        <el-row :gutter="16" style="margin-top:8px;">
          <el-col :span="18" style="text-align:right;padding-right:30px;line-height:32px;">
            <strong>到款金额:</strong>
          </el-col>
          <el-col :span="6">
            <el-input-number
              v-model="orderForm.deposit"
              :min="0"
              :precision="2"
              :controls-position="'right'"
              style="width:100%"
              placeholder="客户已收款金额"
            />
          </el-col>
          <el-col :span="24" style="text-align:right;padding-right:30px;">
            <span style="font-size:12px;color:#909399;">客户到款金额 &gt; 0 后，该 PI 才能生成 CI / PL</span>
          </el-col>
        </el-row>

        <!-- 备注 -->
        <el-divider content-position="left">备注</el-divider>
        <el-form-item label="订单备注" prop="remarks">
          <el-input v-model="orderForm.remarks" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="info" @click="saveOrder('draft')">存为草稿</el-button>
        <el-button type="primary" @click="saveOrder('confirmed')">确认订单</el-button>
      </template>
    </el-dialog>

    <!-- 订单详情抽屉 -->
    <el-drawer 
      v-model="detailDrawerVisible" 
      title="订单详情" 
      size="70%"
      direction="rtl"
    >
      <div v-if="currentOrder.orderid">
        <!-- 订单头部信息 -->
        <el-descriptions :column="2" border>
          <el-descriptions-item label="订单号">{{ currentOrder.orderno }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="getStatusType(currentOrder.status)">
              {{ getStatusText(currentOrder.status) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="客户名称">{{ currentOrder.companyname }}</el-descriptions-item>
          <el-descriptions-item label="联系人">{{ currentOrder.contactname || '-' }}</el-descriptions-item>
          <el-descriptions-item label="订单类型">
            {{ currentOrder.ordertype === 'standard' ? '标准订单' : currentOrder.ordertype === 'sample' ? '样品单' : '维修单' }}
          </el-descriptions-item>
          <el-descriptions-item label="币种">{{ currentOrder.currency || 'USD' }}</el-descriptions-item>
          <el-descriptions-item label="贸易术语">{{ currentOrder.incoterms }}</el-descriptions-item>
          <el-descriptions-item label="付款方式">{{ currentOrder.paymentmethod }}</el-descriptions-item>
          <el-descriptions-item label="总金额">
            <strong>{{ formatCurrency(currentOrder.totalamount, currentOrder.currency) }}</strong>
          </el-descriptions-item>
          <el-descriptions-item label="到款金额">
            <template v-if="Number(currentOrder.deposit || 0) > 0">
              <strong class="text-success">{{ formatCurrency(currentOrder.deposit, currentOrder.currency) }}</strong>
            </template>
            <template v-else>-</template>
          </el-descriptions-item>
          <el-descriptions-item label="签发日期">{{ formatDate(currentOrder.issuedate) }}</el-descriptions-item>
          <el-descriptions-item label="预计发货">{{ formatDate(currentOrder.estimatedshipdate) }}</el-descriptions-item>
          <el-descriptions-item label="实际发货">{{ formatDate(currentOrder.actualshipdate) }}</el-descriptions-item>
          <el-descriptions-item label="目的地">{{ currentOrder.destination || '-' }}</el-descriptions-item>
          <el-descriptions-item label="来源单证">
            <template v-if="currentOrder.sourcedocumentid">
              <span v-if="currentOrder.sourcedocnumber">{{ currentOrder.sourcedocnumber }}</span>
              <span v-else>单证#{{ currentOrder.sourcedocumentid }}</span>
            </template>
            <template v-else>-</template>
          </el-descriptions-item>
          <el-descriptions-item label="创建日期">{{ formatDate(currentOrder.createdate) }}</el-descriptions-item>
          <el-descriptions-item label="更新日期">{{ formatDate(currentOrder.updatedate) }}</el-descriptions-item>
          <el-descriptions-item label="备注" :span="2">{{ currentOrder.remarks || '-' }}</el-descriptions-item>
        </el-descriptions>

        <!-- 产品明细 -->
        <el-divider content-position="left">产品明细</el-divider>
        <el-table :data="currentOrder.items" border size="small">
          <el-table-column prop="productcode" label="产品型号" width="120" />
          <el-table-column prop="description" label="描述" />
          <el-table-column prop="quantity" label="数量" width="80" align="center" />
          <el-table-column prop="unitprice" label="单价" width="100" align="right">
            <template #default="{row}">
              {{ formatCurrency(row.unitprice, currentOrder.currency) }}
            </template>
          </el-table-column>
          <el-table-column label="小计" width="100" align="right">
            <template #default="{row}">
              {{ formatCurrency(row.quantity * row.unitprice, currentOrder.currency) }}
            </template>
          </el-table-column>
        </el-table>

        <!-- 状态历史 -->
        <el-divider content-position="left">状态历史</el-divider>
        <el-timeline>
          <el-timeline-item 
            v-for="(log, index) in statusLogs" 
            :key="index"
            :timestamp="formatDateTime(log.createtime)"
            placement="top"
          >
            <el-tag :type="getStatusType(log.newstatus)">
              {{ getStatusText(log.newstatus) }}
            </el-tag>
            <span style="margin-left:8px;">{{ log.remarks }}</span>
          </el-timeline-item>
        </el-timeline>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { post, get } from '@/api/index'
import { ElMessage, ElMessageBox } from 'element-plus'

export default {
  name: 'OrdersPage',
  
  data() {
    return {
      // 列表数据
      orders: [],
      total: 0,
      loading: false,
      searchKeyword: '',
      filterStatus: '',
      filterType: '',
      currentPage: 1,
      pageSize: 20,
      
      // 对话框
      dialogVisible: false,
      isEdit: false,
      orderFormRef: null,
      orderForm: this.getEmptyOrderForm(),
      orderRules: {
        companyid: [{ required: true, message: '请选择客户', trigger: 'change' }],
        incoterms: [{ required: true, message: '请选择贸易术语', trigger: 'change' }],
        paymentmethod: [{ required: true, message: '请选择付款方式', trigger: 'change' }],
        currency: [{ required: true, message: '请选择币种', trigger: 'change' }],
        issuedate: [{ required: true, message: '请选择签发日期', trigger: 'change' }]
      },
      
      // 客户搜索
      customerSearchText: '',
      contactList: [],
      
      // 详情抽屉
      detailDrawerVisible: false,
      currentOrder: {},
      statusLogs: [],
      
      // 产品列表（用于自动补全）
      productList: [],
      // 响应式
      isMobile: false
    }
  },
  mounted() {
    this.checkMobile()
    this._resizeHandler = () => this.checkMobile()
    window.addEventListener('resize', this._resizeHandler)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this._resizeHandler)
  },

  computed: {
    totalAmount() {
      return this.orderForm.items.reduce((sum, item) => {
        return sum + (item.quantity || 0) * (item.unitprice || 0)
      }, 0)
    }
  },

  created() {
    this.loadOrders()
    this.loadProducts()
    // 支持从报价记录生成订单（带全详细信息：贸易条款/付款方式/目的地/联系人/明细）
    const q = this.$route.query
    if (q.fromQuote) {
      this.showAddDialog()
      if (q.companyid) this.orderForm.companyid = parseInt(q.companyid)
      if (q.companyname) this.customerSearchText = q.companyname
      if (q.currency) this.orderForm.currency = q.currency
      if (q.totalamount) this.orderForm.totalamount = parseFloat(q.totalamount)
      if (q.documentid) this.orderForm.sourcedocumentid = parseInt(q.documentid)
      if (q.incoterms) this.orderForm.incoterms = q.incoterms
      if (q.paymentmethod) this.orderForm.paymentmethod = q.paymentmethod
      if (q.destination) this.orderForm.destination = q.destination
      if (q.remarks) this.orderForm.remarks = q.remarks
      // 联系人：等 loadContacts 完成后再设置（下拉 value 统一 Number 匹配）
      if (q.companyid) {
        this.loadContacts(parseInt(q.companyid)).then(() => {
          if (q.contactid) this.orderForm.contactid = Number(q.contactid)
        })
      }
      // 解析产品列表（含 productid，便于回填型号）
      if (q.products) {
        try {
          const products = JSON.parse(q.products)
          if (Array.isArray(products) && products.length) {
            this.orderForm.items = products.map(p => ({
              productid: p.productid || null,
              productcode: p.productcode || '',
              description: p.description || '',
              quantity: p.quantity || 1,
              unitprice: p.unitprice || 0
            }))
          }
        } catch(e) { console.error('products parse error', e) }
      }
      // 清除 URL query 参数，避免刷新时重复触发
      this.$router.replace({ query: {} }).catch(() => {})
    }
  },

  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth < 768
    },
    // ========== 数据加载 ==========
    async loadOrders(resetPage = false) {
      if (resetPage) this.currentPage = 1
      this.loading = true
      try {
        const res = await post('ordersapi.asp', {
          action: 'list',
          keyword: this.searchKeyword,
          status: this.filterStatus,
          ordertype: this.filterType,
          page: this.currentPage,
          size: this.pageSize
        })

        if (res && res.list) {
          this.orders = res.list
          this.total = res.total || res.list.length
        } else if (Array.isArray(res)) {
          this.orders = res
          this.total = res.length
        } else {
          this.orders = []
          this.total = 0
        }
      } catch (error) {
        console.error('加载订单失败:', error)
        ElMessage.error('加载订单失败')
      } finally {
        this.loading = false
      }
    },
    
    async loadProducts() {
      try {
        const res = await post('products.asp', { action: 'products' })
        // products.asp 返回格式: { "products": [...] }
        this.productList = res.products || res.data?.products || []
      } catch (error) {
        console.error('加载产品列表失败:', error)
      }
    },

    // ========== 订单操作 ==========
    getEmptyOrderForm() {
      return {
        orderid: null,
        orderno: '',
        ordertype: 'standard',
        companyid: null,
        contactid: null,
        incoterms: 'FOB',
        paymentmethod: 'T/T 30% deposit',
        currency: 'USD',
        issuedate: new Date().toISOString().slice(0, 10),
        estimatedshipdate: '',
        destination: '',
        totalamount: 0,
        deposit: 0,
        status: 'draft',
        remarks: '',
        sourcedocumentid: null,
        items: []
      }
    },

    showAddDialog() {
      this.isEdit = false
      this.orderForm = this.getEmptyOrderForm()
      this.customerSearchText = ''
      this.contactList = []
      this.dialogVisible = true
    },

    async editOrder(row) {
      this.isEdit = true
      try {
        const res = await post('ordersapi.asp', {
          action: 'detail',
          orderid: row.orderid
        })
        
        this.orderForm = res.data
        this.customerSearchText = res.data.companyname || ''
        
        // 加载联系人
        if (res.data.companyid) {
          await this.loadContacts(res.data.companyid)
        }
        
        // 加载产品明细
        const itemsRes = await post('ordersapi.asp', {
          action: 'items',
          orderid: row.orderid
        })
        this.orderForm.items = itemsRes.list || []
        
        this.dialogVisible = true
      } catch (error) {
        console.error('加载订单详情失败:', error)
        ElMessage.error('加载订单详情失败')
      }
    },

    async saveOrder(status) {
      try {
        await this.$refs.orderFormRef.validate()
      } catch (error) {
        ElMessage.warning('请填写必要信息')
        return
      }
      
      if (this.orderForm.items.length === 0) {
        ElMessage.warning('请至少添加一个产品')
        return
      }
      
      this.orderForm.status = status
      this.orderForm.totalamount = this.totalAmount
      
      try {
        const action = this.isEdit ? 'update' : 'create'
        const payload = {
          action,
          ...this.orderForm,
          items: JSON.stringify(this.orderForm.items)
        }
        
        const res = await post('ordersapi.asp', payload)
        
        if (res && res.success) {
          ElMessage.success(status === 'draft' ? '草稿已保存' : '订单已确认')
          this.dialogVisible = false
          this.loadOrders()
        } else {
          ElMessage.error(res?.message || '保存失败')
        }
      } catch (error) {
        console.error('保存订单失败:', error)
        ElMessage.error('保存订单失败')
      }
    },

    async confirmOrder(row) {
      try {
        await ElMessageBox.confirm('确认此订单？确认后将无法编辑。', '提示', {
          confirmButtonText: '确认',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const res = await post('ordersapi.asp', {
          action: 'updateStatus',
          orderid: row.orderid,
          status: 'confirmed'
        })
        
        if (res && res.success) {
          ElMessage.success('订单已确认')
          this.loadOrders()
        } else {
          ElMessage.error(res?.message || '操作失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('操作失败')
        }
      }
    },

    async cancelOrder(row) {
      try {
        await ElMessageBox.confirm('确定要取消此订单吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const res = await post('ordersapi.asp', {
          action: 'updateStatus',
          orderid: row.orderid,
          status: 'cancelled'
        })
        
        if (res && res.success) {
          ElMessage.success('订单已取消')
          this.loadOrders()
        } else {
          ElMessage.error(res?.message || '操作失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('操作失败')
        }
      }
    },

    // ========== 更新订单状态（推进到下一阶段） ==========
    async updateStatus(row) {
      // 每个状态的下一步推进方向（不含取消，取消用独立按钮）
      const nextStep = {
        draft:       { label: '确认订单', value: 'confirmed' },
        confirmed:   { label: '开始生产', value: 'producing' },
        producing:   { label: '标记发货', value: 'shipped' },
        shipped:     { label: '标记完成', value: 'completed' }
      }

      const target = nextStep[row.status]
      if (!target) {
        ElMessage.warning('当前状态无可用操作')
        return
      }

      try {
        await ElMessageBox.confirm(
          `将订单 "${row.orderno}" 从"${this.getStatusText(row.status)}"更新为"${target.label}"？`,
          '更新订单状态',
          { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' }
        )

        const res = await post('ordersapi.asp', {
          action: 'updateStatus',
          orderid: row.orderid,
          status: target.value
        })

        if (res && res.success) {
          ElMessage.success('状态已更新')
          this.loadOrders()
        } else {
          ElMessage.error(res?.message || '操作失败')
        }
      } catch (error) {
        if (error !== 'cancel' && String(error).indexOf('cancel') === -1) {
          ElMessage.error('操作失败')
        }
      }
    },

    // ========== 详情查看 ==========
    async viewOrder(row) {
      try {
        const res = await post('ordersapi.asp', {
          action: 'detail',
          orderid: row.orderid
        })
        
        this.currentOrder = res.data
        
        // 加载产品明细
        const itemsRes = await post('ordersapi.asp', {
          action: 'items',
          orderid: row.orderid
        })
        this.currentOrder.items = itemsRes.list || []
        
        // 加载状态日志
        const logsRes = await post('ordersapi.asp', {
          action: 'statusLogs',
          orderid: row.orderid
        })
        this.statusLogs = logsRes.list || []
        
        this.detailDrawerVisible = true
      } catch (error) {
        console.error('加载订单详情失败:', error)
        ElMessage.error('加载订单详情失败')
      }
    },

    // ========== 客户搜索 ==========
    async searchCustomers(query, cb) {
      if (!query) {
        cb([])
        return
      }
      
      try {
        const res = await post('customersapi.asp', {
          action: 'searchcustomers',
          keyword: query
        })

        const results = (res || []).map(c => ({
          ...c,
          value: c.companyname
        }))
        
        cb(results)
      } catch (error) {
        console.error('搜索客户失败:', error)
        cb([])
      }
    },

    async handleCustomerSelect(item) {
      this.orderForm.companyid = item.companyid
      this.customerSearchText = item.companyname
      await this.loadContacts(item.companyid)
    },

    async loadContacts(companyid) {
      try {
        const res = await post('customersapi.asp', {
          action: 'getcontactlist',
          companyid: companyid
        })

        this.contactList = res || []
      } catch (error) {
        console.error('加载联系人失败:', error)
        this.contactList = []
      }
    },

    // ========== 产品搜索 ==========
    searchProducts(query, cb) {
      const results = this.productList
        .filter(p => 
          p.productcode.toLowerCase().includes(query.toLowerCase()) ||
          p.productname.toLowerCase().includes(query.toLowerCase())
        )
        .slice(0, 10)
        .map(p => ({
          ...p,
          value: p.productcode
        }))
      
      cb(results)
    },

    handleProductSelect(index, item) {
      this.orderForm.items[index].productid = item.productid
      this.orderForm.items[index].productcode = item.productcode
      this.orderForm.items[index].description = item.productname
      this.orderForm.items[index].unitprice = item.unitprice || 0
    },

    addProductRow() {
      this.orderForm.items.push({
        productid: null,
        productcode: '',
        description: '',
        quantity: 1,
        unitprice: 0
      })
    },

    removeProductRow(index) {
      this.orderForm.items.splice(index, 1)
    },

    // ========== 工具函数 ==========
    formatDate(date) {
      if (!date) return '-'
      return date.slice(0, 10)
    },

    formatDateTime(datetime) {
      if (!datetime) return '-'
      return datetime.replace('T', ' ').slice(0, 19)
    },

    formatCurrency(amount, currency) {
      if (!amount) return '-'
      const symbol = { USD: '$', EUR: '€', CNY: '¥' }[currency] || ''
      return symbol + parseFloat(amount).toFixed(2)
    },

    getStatusType(status) {
      const map = {
        draft: 'info',
        confirmed: '',
        producing: 'warning',
        shipped: 'success',
        completed: 'success',
        cancelled: 'danger'
      }
      return map[status] || 'info'
    },

    getStatusText(status) {
      const map = {
        draft: '草稿',
        confirmed: '已确认',
        producing: '生产中',
        shipped: '已发货',
        completed: '已完成',
        cancelled: '已取消'
      }
      return map[status] || '未知'
    },

    isOverdue(date) {
      if (!date) return false
      return new Date(date) < new Date()
    }
  }
}
</script>

<style scoped>
.orders-container {
  padding: 20px;
}

.filter-card {
  margin-bottom: 16px;
}

.list-card {
  margin-bottom: 16px;
}

.product-row {
  margin-bottom: 8px;
  padding: 8px;
  background: #f5f7fa;
  border-radius: 4px;
}

.subtotal {
  font-weight: bold;
  color: #409eff;
}

.total-amount {
  font-size: 18px;
  color: #f56c6c;
}

.overdue {
  color: #f56c6c;
  font-weight: bold;
}

/* ========== 移动端卡片视图 ========== */
.mobile-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
}
.order-card {
  background: #fff;
  border-radius: 8px;
  padding: 12px 14px;
  box-shadow: 0 1px 3px rgba(0,0,0,.08);
  cursor: pointer;
  transition: box-shadow .2s;
}
.order-card:active {
  box-shadow: 0 2px 8px rgba(0,0,0,.12);
}
.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.card-order-no {
  font-weight: 600;
  font-size: 15px;
  color: #303133;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.card-body {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 16px;
  margin-bottom: 10px;
}
.card-field {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.field-label {
  font-size: 12px;
  color: #909399;
  flex-shrink: 0;
  white-space: nowrap;
}
.field-value {
  font-size: 13px;
  color: #606266;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fw-bold { font-weight: 600; }
.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
}
.card-date {
  font-size: 12px;
  color: #909399;
}
.card-actions {
  display: flex;
  gap: 4px;
}
</style>
